import { Link } from "react-router-dom";
import {
  EDU_EDITION_ORDER,
  EDU_EDITIONS,
  type EduEditionId,
} from "@itaas/edu-editions";
import {
  HOSPITOL_EDITION_ORDER,
  HOSPITOL_EDITIONS,
  HOSPITOL_INCLUDED_SERVICES,
  HOSPITOL_PREMIUM_ADDONS,
  type HospitolEditionId,
} from "@itaas/hospitol-editions";
import type { PricingQuoteResponse, ProductCard, ProductPromotion } from "@itaas/pep-contracts";
import { ProductPromotionBadge } from "../commercial/ProductPromotionBadge";
import { PromoPriceUsd } from "../commercial/PromoPrice";
import type { PepLocale } from "../../i18n/types";
import {
  discountedAmount,
  isPromotionActive,
  resolveActivePromotion,
} from "../../utils/product-promotion";
import {
  buildConfigureHrefForProduct,
  commercialCtaLabelForProduct,
} from "../../utils/commercial-cta";

interface ProductEditionShowcaseProps {
  product: ProductCard;
  prices: Record<string, PricingQuoteResponse>;
  locale: PepLocale;
}

function editionShortName(slug: string, displayName: string): string {
  if (slug === "edu") return displayName.replace("ITaaS Edu ", "");
  if (slug === "hospitol") return displayName.replace("Hospitol ", "");
  return displayName;
}

function renderPrice(
  editionId: string,
  monthlyUsd: number | null,
  promotion: ProductPromotion | null | undefined,
  quote?: PricingQuoteResponse,
) {
  if (monthlyUsd === null) {
    return <span className="pep-edition-showcase__price">Sur devis</span>;
  }
  const activePromo = resolveActivePromotion(promotion, editionId);
  if (quote?.originalAmountFormatted && quote.amountFormatted) {
    return (
      <span className="pep-edition-showcase__price">
        <PromoPriceUsd
          originalUsd={monthlyUsd}
          promoUsd={discountedAmount(monthlyUsd, activePromo!.discountPercent)}
        />
        <span className="d-block small text-secondary mt-1">USD/mois</span>
      </span>
    );
  }
  if (activePromo) {
    return (
      <span className="pep-edition-showcase__price">
        <PromoPriceUsd
          originalUsd={monthlyUsd}
          promoUsd={discountedAmount(monthlyUsd, activePromo.discountPercent)}
        />
        <span className="d-block small text-secondary mt-1">USD/mois</span>
      </span>
    );
  }
  return (
    <span className="pep-edition-showcase__price">
      {monthlyUsd} USD/mois
    </span>
  );
}

export function ProductEditionShowcase({ product, prices, locale }: ProductEditionShowcaseProps) {
  const isEdu = product.slug === "edu";
  const isHospitol = product.slug === "hospitol";
  if (!isEdu && !isHospitol) return null;

  const title =
    locale === "en" ? "Choose your edition" : "Choisissez votre édition";
  const subtitle =
    locale === "en"
      ? "Each edition includes everything from the previous tier. Higher-tier features can be previewed during your trial."
      : "Chaque édition inclut les modules de la précédente. Les fonctionnalités des éditions supérieures sont visibles en aperçu pendant l'essai.";

  return (
    <section className="mb-5 pep-edition-showcase">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <h2 className="h4 fw-bold mb-0">{title}</h2>
        {isPromotionActive(product.promotion) ? (
          <ProductPromotionBadge promotion={product.promotion} locale={locale} />
        ) : null}
      </div>
      <p className="text-secondary mb-4">{subtitle}</p>

      <div className="row g-3">
        {isEdu
          ? EDU_EDITION_ORDER.map((id: EduEditionId) => {
              const ed = EDU_EDITIONS[id];
              const modules = ed.newInEdition ?? ed.modules;
              return (
                <div className="col-md-6 col-xl-3" key={id}>
                  <div
                    className={`pep-edition-showcase__card h-100${ed.highlight ? " is-recommended" : ""}`}
                  >
                    {ed.highlight ? (
                      <span className="pep-edition-showcase__badge">Populaire</span>
                    ) : null}
                    <h3 className="pep-edition-showcase__name">
                      {editionShortName(product.slug, ed.displayName)}
                    </h3>
                    <p className="pep-edition-showcase__tagline">{ed.tagline}</p>
                    {renderPrice(id, ed.monthlyUsd, product.promotion, prices[id])}
                    <p className="pep-edition-showcase__capacity">{ed.maxStudentsLabel}</p>
                    <ul className="pep-edition-showcase__modules">
                      {modules.slice(0, 6).map((m) => (
                        <li key={m}>{m}</li>
                      ))}
                      {modules.length > 6 ? (
                        <li className="text-secondary">+ autres modules</li>
                      ) : null}
                    </ul>
                  </div>
                </div>
              );
            })
          : HOSPITOL_EDITION_ORDER.map((id: HospitolEditionId) => {
              const ed = HOSPITOL_EDITIONS[id];
              const modules = ed.newInEdition ?? ed.poles;
              return (
                <div className="col-md-6 col-xl-4" key={id}>
                  <div
                    className={`pep-edition-showcase__card h-100${ed.highlight ? " is-recommended" : ""}`}
                  >
                    {ed.highlight ? (
                      <span className="pep-edition-showcase__badge">Populaire</span>
                    ) : null}
                    <h3 className="pep-edition-showcase__name">
                      {editionShortName(product.slug, ed.displayName)}
                    </h3>
                    <p className="pep-edition-showcase__tagline">{ed.tagline}</p>
                    {renderPrice(id, ed.monthlyUsd, product.promotion, prices[id])}
                    <p className="pep-edition-showcase__capacity">{ed.maxUsersLabel}</p>
                    <ul className="pep-edition-showcase__modules">
                      {modules.slice(0, 6).map((m) => (
                        <li key={m}>{m}</li>
                      ))}
                    </ul>
                    <p className="pep-edition-showcase__ideal small text-secondary mb-0">
                      Idéal pour : {ed.idealFor.slice(0, 2).join(", ")}
                      {ed.idealFor.length > 2 ? "…" : ""}
                    </p>
                  </div>
                </div>
              );
            })}
      </div>

      {isHospitol ? (
        <div className="row g-3 mt-2">
          <div className="col-md-7">
            <div className="pep-product-included h-100">
              <h3 className="h6 fw-semibold mb-2">
                {locale === "en" ? "Included in all editions" : "Inclus dans toutes les éditions"}
              </h3>
              <ul className="small mb-0 ps-3">
                {HOSPITOL_INCLUDED_SERVICES.map((service) => (
                  <li key={service}>{service}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="col-md-5">
            <div className="pep-product-included pep-product-included--addons h-100">
              <h3 className="h6 fw-semibold mb-2">
                {locale === "en" ? "Premium add-ons (optional)" : "Modules premium (option)"}
              </h3>
              <ul className="small mb-0 ps-3 text-secondary">
                {HOSPITOL_PREMIUM_ADDONS.map((addon) => (
                  <li key={addon}>{addon}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : null}

      {product.trialDays ? (
        <div className="text-center mt-4">
          <Link className="btn btn-primary btn-lg px-4" to={buildConfigureHrefForProduct(product)}>
            {commercialCtaLabelForProduct(product, locale)}
          </Link>
        </div>
      ) : null}
    </section>
  );
}
